import { useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { NewsletterPopup } from './NewsletterPopup';
import { Navbar } from './Navbar';
import { Footer } from './Footer';
import LiveNotification from './LiveNotification';
import { Toast } from './common/Toast';
import UpgradeModal from './common/UpgradeModal';
import { useAccess } from '@/contexts/AccessContext';

export function Layout() {
  const [showPopup, setShowPopup] = useState(false);
  const [notificationType, setNotificationType] = useState<'discount' | 'social'>('discount');
  const { betaExpired } = useAccess();
  const [dismissedBetaBanner, setDismissedBetaBanner] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem('pv.betaExpired.dismissed') === '1';
  });

  const showBetaBanner = useMemo(() => {
    if (typeof window === 'undefined') return false;
    if (!betaExpired) return false;
    if (dismissedBetaBanner) return false;
    const seenKey = 'pv.betaExpired.seenThisSession';
    if (window.sessionStorage.getItem(seenKey) === '1') return false;
    return true;
  }, [betaExpired, dismissedBetaBanner]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!showBetaBanner) return;
    window.sessionStorage.setItem('pv.betaExpired.seenThisSession', '1');
  }, [showBetaBanner]);

  useEffect(() => {
    const hasSeenPopup = localStorage.getItem('hasSeenPopup');
    if (!hasSeenPopup) {
      const timer = setTimeout(() => {
        setShowPopup(true);
        localStorage.setItem('hasSeenPopup', 'true');
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Switch between discount and social notifications every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setNotificationType(prev => prev === 'discount' ? 'social' : 'discount');
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <Navbar />

      {showBetaBanner ? (
        <div className="sticky top-[72px] z-40 border-b border-yellow-800/60 bg-black/80 backdrop-blur">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-yellow-200">Your beta access has ended</div>
                <div className="mt-1 text-xs text-neutral-200">
                  You’ve been using PropViper’s full calculator, trading plan, and journal features during the beta.
                  <br />
                  To continue using these features across devices and keep your trading data in sync, upgrade to a paid plan.
                  <br />
                  Your existing data is محفوظ and will remain available.
                </div>
                <div className="mt-2 text-[11px] text-neutral-400">Used by serious traders who want structure and consistency.</div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-end">
                <a
                  href="/pricing?plan=pro"
                  className="bg-yellow-400 text-black font-semibold px-4 py-2 rounded-xl hover:bg-yellow-300 transition text-xs text-center"
                >
                  Continue with Pro
                </a>
                <a
                  href="/pricing"
                  className="bg-neutral-800/60 hover:bg-neutral-800 text-neutral-100 border border-neutral-700 px-4 py-2 rounded-xl text-xs font-semibold transition text-center"
                >
                  View pricing
                </a>
                <button
                  type="button"
                  onClick={() => {
                    try {
                      window.localStorage.setItem('pv.betaExpired.dismissed', '1');
                    } finally {
                      setDismissedBetaBanner(true);
                    }
                  }}
                  className="bg-neutral-950 hover:bg-neutral-900 text-neutral-200 border border-neutral-800 px-4 py-2 rounded-xl text-xs font-semibold transition"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <main>
        <Outlet />
      </main>

      <div className="h-1 bg-gradient-to-r from-yellow-600 via-yellow-400 to-yellow-600 opacity-70" />
      <Footer />

      {showPopup && <NewsletterPopup />}

      <LiveNotification type={notificationType} />

      <Toast />

      <UpgradeModal />
    </>
  );
}
